import type { GameBoardOverlayInterface } from "@/game/context/GameBoardOverlayContext";
import { configCheckerHighlight } from "@/game/utils/boardHighligts";
import {
  CounterDisplayType,
  getCheckerPositions,
  renderCheckerStack
} from "@/game/utils/checkerDisplayUtils";
import type { GameState } from "@nextgammon/shared";
import { Constants } from "@nextgammon/shared";
import { useEffect, useMemo } from "react";

import { Container, Text } from "@pixi/react";
import { Assets, Rectangle, TextStyle } from "pixi.js";

interface GameBoardBarProps {
  gameState: GameState;
  selectedPointIndex: number | null;
  setSelectedPointIndex: (index: number) => void;
  ownerPlayerColor: string;
  offBoardArea: [number, number, number, number];
  boardScale: number;
  checkerTexture: string;
  checkerCount: number; // This is the number of checkers that are on the board
  checkerDisplayCount: number; // This is the number of checkers that can be displayed on the board area
  displayCounter: CounterDisplayType;
  overlayInterface: GameBoardOverlayInterface | null;
  onClick: (playerColor: number) => void;
}

export function GameBoardBar({
  gameState,
  selectedPointIndex,
  setSelectedPointIndex,
  ownerPlayerColor,
  offBoardArea,
  boardScale,
  checkerTexture,
  checkerCount,
  checkerDisplayCount,
  displayCounter,
  overlayInterface,
  onClick
}: GameBoardBarProps) {
  // Load the checker texture
  const texture = useMemo(
    () => (checkerTexture ? Assets.get(checkerTexture) : null),
    [checkerTexture]
  );

  // Load selection highlight texture for the top checker
  const selectedCheckerTexture = useMemo(() => Assets.get("highlight-checker"), []);

  if (!gameState.bar) return null;

  const barIndex =
    ownerPlayerColor === Constants.PLAYER_WHITE
      ? Constants.BAR_INDEX_WHITE
      : Constants.BAR_INDEX_BLACK;
  const barCount =
    ownerPlayerColor === Constants.PLAYER_WHITE ? gameState.bar.white : gameState.bar.black;
  const isBarSelected =
    selectedPointIndex === Constants.BAR_INDEX_WHITE ||
    selectedPointIndex === Constants.BAR_INDEX_BLACK;
  const canSelectBar = gameState.currentPlayerColor === ownerPlayerColor && barCount > 0;
  // Determine the exact checker position
  const checkerPosition = useMemo(() => {
    if (!offBoardArea || !texture) return [];
    return getCheckerPositions(
      offBoardArea,
      Math.max(1, Math.min(checkerCount, checkerDisplayCount)),
      texture.height * boardScale,
      displayCounter === CounterDisplayType.DisplayedOnBottom
    );
  }, [offBoardArea, checkerCount, displayCounter, boardScale, texture]);
  const topCheckerPosition = checkerPosition.length
    ? checkerPosition[checkerPosition.length - 1]
    : null;

  const countLabel = barCount ? `x${barCount.toString()}` : "";
  const countLabelSize = 72 * boardScale;
  const clickRegion = offBoardArea
    ? new Rectangle(offBoardArea[0], offBoardArea[1], offBoardArea[2], offBoardArea[3])
    : null;

  // Show or hide the selected state sprite
  useEffect(() => {
    if (!overlayInterface || !selectedCheckerTexture || !offBoardArea || !topCheckerPosition)
      return;

    const key = `selected-bar-${barIndex}`;
    if (isBarSelected && canSelectBar) {
      const highlight = configCheckerHighlight(
        selectedCheckerTexture,
        topCheckerPosition,
        boardScale
      );

      overlayInterface.addSpriteWithTween(key, highlight.sprite, highlight.group, true);
    } else {
      overlayInterface.removeSprite(key);
    }
    // Cleanup on unmount
    return () => {
      overlayInterface.removeSprite(key);
    };
  }, [selectedPointIndex, offBoardArea, boardScale, selectedCheckerTexture, overlayInterface]);

  return (
    <Container hitArea={clickRegion} eventMode="static" pointerdown={onClick}>
      {texture &&
        offBoardArea &&
        renderCheckerStack(
          texture,
          offBoardArea,
          Math.min(checkerCount, checkerDisplayCount),
          boardScale,
          boardScale,
          displayCounter === CounterDisplayType.DisplayedOnBottom
        )}
      {displayCounter !== CounterDisplayType.NotDisplayed && (
        <Text
          text={countLabel}
          x={offBoardArea[0] + offBoardArea[2] / 2}
          y={
            offBoardArea[1] +
            (displayCounter === CounterDisplayType.DisplayedOnBottom ? offBoardArea[3] : 0)
          }
          anchor={[0.5, displayCounter === CounterDisplayType.DisplayedOnBottom ? 1 : 0]}
          style={
            new TextStyle({
              fontFamily: "Arial",
              fontSize: countLabelSize,
              fill: 0xff0000,
              stroke: 0x000000,
              strokeThickness: 2
            })
          }
        />
      )}
    </Container>
  );
}
